import { createSelector, createSlice } from "@reduxjs/toolkit";
import { UserSearchResultDTO } from "models/dto/user";
import { SimpleSetter, SliceActions } from "store";

/**
 * State for user search data stored in Redux.
 * Contains raw user search results from the API, which can be used
 * for CRUD operations and displaying user sector information.
 */
export type UserSearchState = {
	users: UserSearchResultDTO[];
	loading: boolean;
	error: string;
};

const initialState: UserSearchState = {
	users: [],
	loading: false,
	error: "",
};

export const UserSearchSlice = createSlice({
	name: "userSearch",
	initialState,
	reducers: {
		SetUsers: SimpleSetter<UserSearchState, UserSearchResultDTO[]>("users"),
		SetLoading: SimpleSetter<UserSearchState, boolean>("loading"),
		SetError: SimpleSetter<UserSearchState, string>("error"),
		ClearUsers: (state) => {
			state.users = [];
			state.error = "";
		},
	},
	selectors: {
		selectUsers: (state) => state.users,
		selectLoading: (state) => state.loading,
		selectError: (state) => state.error,
	},
});

/**
 * Computed selector to get sector information for a specific user.
 * Extracts all unique sector names and IDs from the user's roles.
 * 
 * @param state - Redux state
 * @param userId - User ID to look up
 * @returns Object with sectorNames and sectorIds arrays, or null if user not found
 */
export const SelectUserSectors = createSelector(
	[UserSearchSlice.selectors.selectUsers, (s, userId?: string) => userId],
	(users, userId) => {
		if (!userId) return null;

		const user = users.find((u) => u.userData.id === userId);
		if (!user) return null;

		const sectorNames = new Set<string>();
		const sectorIds = new Set<string>();

		user.userRoles.forEach((userRole) => {
			if (userRole.containmentSectorName) {
				sectorNames.add(userRole.containmentSectorName);
			}
			if (userRole.containmentSectorId) {
				sectorIds.add(userRole.containmentSectorId);
			}
		});

		return {
			sectorNames: Array.from(sectorNames),
			sectorIds: Array.from(sectorIds),
		};
	}
);

/**
 * Computed selector to get a user by ID.
 * 
 * @param state - Redux state
 * @param id - User ID to look up
 * @returns UserSearchResultDTO or null if not found
 */
export const SelectUserById = createSelector(
	[UserSearchSlice.selectors.selectUsers, (s, id?: string) => id],
	(users, id) => users.find((u) => u.userData.id === id) || null
);

export type UserSearchAction = SliceActions<typeof UserSearchSlice>;
